import { checkIcorDevice, DEVICE_STATUS } from '@/entities/device';

import {
  STUDY_STATUS,
  STUDY_PENDING_COMMAND,
  checkHasSentRefCode,
  isManagedC1cDevice,
  isC1cStudy,
  isCardioOneFacility,
} from '@/entities/study';
import { useMergeState } from '@/shared/lib/customHooks';
import dayjs from 'dayjs';
import _ from 'lodash';
import React, { useImperativeHandle, useMemo, useRef } from 'react';
import type { Study, StudyCardProps } from './studyCard.types';

/**
 * Backs a study card: it derives the display flags the card renders from the study and drives the ECG strip image and gallery state. Memoizes isBiodirectStudy from biofluxDirect, resolves hasSentRefCode and isIcorDevice, gates the stopping alert on remaining minutes with a pending STOP_STUDY command or an in-flight stop for non-iCOR devices, tracks image load and error, and exposes the last-evaluation-event polling through ref.
 *
 * @param study - Study rendered by the card.
 * @param supportFacilities - Facilities from the support store, used to tell whether the study belongs to CardioOne.
 * @param handleGetLastEvaluationEvent - Loader fetching the study's last evaluation event.
 * @param ref - Imperative handle receiving the polling starter.
 * @returns The card state, derived display flags and the remaining-minutes, ECG-button and gallery helpers.
 */
export const useStudyCard = (
  { study, handleGetLastEvaluationEvent, supportFacilities = [] }: StudyCardProps,
  ref: React.Ref<any>,
) => {
  const [state, setState] = useMergeState({
    isLoading: true,
    isLoadedImageError: false,
    isOpenSolutionsModal: false,
    displayedPatientData: undefined,
  });

  const retryGetEcgCount = useRef(0);
  const getEcgInterval = useRef<ReturnType<typeof setInterval> | null>(null);

  /**
   * Holds whether the card's study is a Biodirect study. Memoizes an emptiness check on study.biofluxDirect, recomputing only when that field changes.
   *
   * @returns True when the study carries Biodirect data.
   */
  const isBiodirectStudy = useMemo(() => !_.isEmpty(study.biofluxDirect), [study.biofluxDirect]);

  const hasSentRefCode = checkHasSentRefCode(study);

  const isIcorDevice = checkIcorDevice(study.device);

  const isCardioOneFacilityStudy = isCardioOneFacility(study.facility?.id, supportFacilities);

  const isC1cProviderStudy = isC1cStudy(study);

  const isManagedC1cDeviceStudy = isManagedC1cDevice(isC1cProviderStudy, study.deviceType);

  /**
   * Reacts to a successful ECG strip image load on the study card. Clears the loading flag, but only while the card still considers itself loading.
   *
   * @returns Nothing.
   */
  const onImageLoad = () => {
    if (state.isLoading) {
      setState({ isLoading: false });
    }
  };

  /**
   * Reacts to a failed ECG strip image load on the study card. Clears the loading flag and raises isLoadedImageError, but only while the card still considers itself loading.
   *
   * @returns Nothing.
   */
  const onImageError = () => {
    if (state.isLoading) {
      setState({ isLoading: false, isLoadedImageError: true });
    }
  };

  /**
   * Computes how many minutes of a study's configured duration are still left. Measures the fractional minutes elapsed since start with dayjs, counts them only when the study has a stop time and the difference is positive, and subtracts that from duration.
   *
   * @param study - Study supplying start, stop and duration.
   * @returns The remaining minutes, negative once the duration is exceeded.
   */
  const getRemainingMinutes = (study: Study) => {
    const { start, stop, duration } = study;
    const diff = dayjs().diff(start, 'minute', true);
    const minutesDiff = stop && diff > 0 ? diff : 0;
    return duration - minutesDiff;
  };

  const isStoppingStudy = study.isStopping && _.isEmpty(study.uploadStatus);

  /**
   * Holds whether the stopping banner belongs on the study card. Requires remaining duration, a pending STOP_STUDY command or an in-flight stop, and a non-iCOR device.
   *
   * @returns True when the stopping alert must be displayed.
   */
  const isShowStoppingAlert =
    getRemainingMinutes(study) > 0 &&
    (study.pendingCommand?.command === STUDY_PENDING_COMMAND.STOP_STUDY || isStoppingStudy) &&
    !isIcorDevice;

  const isShowAbortingAlert = study.isAborting && !isIcorDevice;

  /**
   * Reports whether the study card's Get ECG button must stay disabled. Treats ECG retrieval as available only while lastStudyHistory.status is STARTED or RESUMED and the device status is not OFFLINE, then returns the negation.
   *
   * @param study - Study whose last history status and device status are checked.
   * @returns True when ECG retrieval is unavailable.
   */
  const isDisabledGetECGButton = (study: Study) => {
    const stateGetECG = [STUDY_STATUS.STARTED, STUDY_STATUS.RESUMED];
    const isEnableGetEcg =
      stateGetECG.includes(study.lastStudyHistory?.status) &&
      study.device.status !== DEVICE_STATUS.OFFLINE;
    return !isEnableGetEcg;
  };

  /**
   * Polls a study's last evaluation event after an ECG request from the card. Fires an immediate fetch and clears the interval when one is already running, resets the retry counter, then starts a 30 second interval that fetches up to three more times before clearing itself.
   *
   * @param id - Study id whose last evaluation event is fetched.
   * @returns Nothing.
   */
  const handleGetLastEvaluationEventInterval = (id: string) => {
    // *: Always clear previous interval
    if (getEcgInterval.current) {
      // *: Immediate API call
      handleGetLastEvaluationEvent(id);

      clearInterval(getEcgInterval.current);
      getEcgInterval.current = null;
    }

    // *: Reset retry count
    retryGetEcgCount.current = 0;

    // *: Start interval for next 3 attempts
    getEcgInterval.current = setInterval(() => {
      retryGetEcgCount.current += 1;
      if (retryGetEcgCount.current <= 3) {
        handleGetLastEvaluationEvent(id);
      } else {
        clearInterval(getEcgInterval.current ?? undefined);
        getEcgInterval.current = null;
      }
    }, 30000);
  };

  /**
   * Builds the ECG strip gallery items shown on a study card. Drops every strip whose URL contains all.svg, then maps each remaining strip to an entry reusing the URL as both original and thumbnail with a one-based position over total description.
   *
   * @param stripImgs - Strip image URLs from the last evaluation event.
   * @returns Gallery items for the retained strips.
   */
  const createItems = (stripImgs: string[]) => {
    /**
     * Holds the strip URLs eligible for the gallery. Filters out every strip whose URL contains all.svg, which is the combined overview image.
     *
     * @returns The strip URLs excluding the combined overview image.
     */
    const notIncludedAllStripImgs = stripImgs.filter((x) => !x.includes('all.svg'));
    return notIncludedAllStripImgs.map((stripImg, index) => ({
      original: stripImg,
      thumbnail: stripImg,
      description: `${index + 1}/${notIncludedAllStripImgs.length}`,
    }));
  };

  useImperativeHandle(ref, () => ({
    handleGetLastEvaluationEventInterval,
  }));

  return {
    state,
    setState,
    isBiodirectStudy,
    hasSentRefCode,
    isIcorDevice,
    isCardioOneFacilityStudy,
    isC1cProviderStudy,
    isManagedC1cDeviceStudy,
    onImageLoad,
    onImageError,
    getRemainingMinutes,
    isStoppingStudy,
    isShowStoppingAlert,
    isShowAbortingAlert,
    isDisabledGetECGButton,
    createItems,
  };
};
